import { Activity, Bot, CheckCircle2, Clock, GitPullRequest, Server } from 'lucide-react';
import { useAgentStatus, type AgentStatus } from '../lib/data';
import { Skeleton, SkeletonListCard } from './Skeleton';
import { DataTable, type Column } from './DataTable';

type InFlight = AgentStatus['inFlight'][number];
type RecentReview = AgentStatus['recentReviews'][number];

function formatAge(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return 'just now';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

function conclusionBadge(conclusion: string) {
  const map: Record<string, string> = {
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    changes_requested: 'bg-amber-50 text-amber-700 border-amber-200',
    commented: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return map[conclusion] ?? 'bg-slate-50 text-slate-600 border-slate-200';
}

function StatCard({ icon: Icon, label, value, tone }: { icon: typeof Bot; label: string; value: number | string; tone: string }) {
  return (
    <div className="bg-white border border-[#EDEDEB] rounded-xl p-4 flex items-center gap-4">
      <div className={`p-3 rounded-lg border ${tone}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider font-semibold">{label}</div>
        <div className="text-xl font-bold text-[#191919]">{value}</div>
      </div>
    </div>
  );
}

export function AgentRunners() {
  const { status, loading } = useAgentStatus();

  if (loading && !status) {
    return (
      <div className="space-y-6 animate-fadeIn" aria-busy="true" aria-label="Loading runner status">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-[#EDEDEB] rounded-xl p-4 flex items-center gap-4">
              <Skeleton width={44} height={44} rounded={8} />
              <div className="space-y-2">
                <Skeleton width={80} height={10} rounded={3} />
                <Skeleton width={50} height={20} rounded={4} />
              </div>
            </div>
          ))}
        </div>
        <SkeletonListCard rows={4} titleWidth={120} />
        <SkeletonListCard rows={4} titleWidth={140} />
      </div>
    );
  }

  const s: AgentStatus | null = status;
  const completedCount = s?.recentReviews.length ?? 0;
  const inFlight = s?.inFlight ?? [];
  const reviews = s?.recentReviews ?? [];

  const inFlightColumns: Column<InFlight>[] = [
    {
      key: 'repo',
      header: 'Task',
      render: (t) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <GitPullRequest className="w-4 h-4 shrink-0" style={{ color: 'var(--ink-secondary)' }} />
          <span className="font-mono text-xs truncate" style={{ color: 'var(--ink)' }}>{t.repo}#{t.prNumber}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      render: (t) => (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${
          t.status === 'running'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-blue-50 text-blue-700 border-blue-200'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${t.status === 'running' ? 'bg-emerald-500 animate-pulse' : 'bg-blue-400'}`} />
          {t.status}
        </span>
      ),
    },
    { key: 'age', header: 'Age', width: '90px', render: (t) => <span className="text-[11px] font-mono" style={{ color: 'var(--ink-secondary)' }}>{formatAge(t.ageMs)}</span> },
  ];

  const reviewColumns: Column<RecentReview>[] = [
    {
      key: 'pr',
      header: 'Review',
      render: (r) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <GitPullRequest className="w-4 h-4 shrink-0" style={{ color: 'var(--ink-secondary)' }} />
          <div className="min-w-0">
            <div className="font-mono text-xs truncate" style={{ color: 'var(--ink)' }}>{r.repo ? `${r.repo}#${r.prNumber ?? '?'}` : r.prId}</div>
            <div className="text-[11px] truncate" style={{ color: 'var(--ink-secondary)' }}>{r.title ?? 'Untitled PR'}</div>
          </div>
        </div>
      ),
    },
    { key: 'score', header: 'Score', width: '90px', render: (r) => <span className="font-mono text-xs" style={{ color: 'var(--ink)' }}><span className="font-bold">{r.score}</span></span> },
    {
      key: 'conclusion',
      header: 'Conclusion',
      width: '150px',
      render: (r) => <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${conclusionBadge(r.conclusion)}`}>{r.conclusion}</span>,
    },
    { key: 'time', header: 'Time', width: '90px', render: (r) => <span className="text-[11px] font-mono" style={{ color: 'var(--ink-secondary)' }}>{formatAge(Date.now() - new Date(r.timestamp).getTime())}</span> },
  ];

  return (
    <div className="space-y-6 animate-fadeIn" id="agents-runners-view">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <StatCard icon={Clock} label="Pending (queued)" value={s?.pending ?? 0} tone="bg-blue-50 text-blue-600 border-blue-100" />
        <StatCard icon={Activity} label="Running" value={s?.running ?? 0} tone="bg-emerald-50 text-emerald-600 border-emerald-100" />
        <StatCard icon={CheckCircle2} label="Recent reviews" value={completedCount} tone="bg-purple-50 text-purple-600 border-purple-100" />
        <StatCard icon={Server} label="Pipeline" value="Live" tone="bg-slate-50 text-slate-600 border-slate-200" />
      </div>

      <DataTable
        rows={inFlight}
        columns={inFlightColumns}
        rowKey={(t) => t.taskId}
        searchKeys={[(t) => `${t.repo}#${t.prNumber}`]}
        searchPlaceholder="Search tasks…"
        pageSize={10}
        skeletonCols={['60%', '120px', '90px']}
        emptyState={<>No tasks in flight. The Edge Runner is idle, waiting for a webhook.</>}
        toolbar={<span className="inline-flex items-center gap-1.5 text-xs font-semibold"><Bot className="w-3.5 h-3.5" />In-flight tasks</span>}
      />

      <DataTable
        rows={reviews}
        columns={reviewColumns}
        rowKey={(r) => `${r.prId}:${r.timestamp}`}
        searchKeys={[(r) => r.repo ?? '', (r) => r.title ?? '']}
        searchPlaceholder="Search reviews…"
        pageSize={10}
        skeletonCols={['60%', '90px', '150px', '90px']}
        emptyState={<>No reviews yet. Dispatch a webhook to see results here.</>}
        toolbar={<span className="inline-flex items-center gap-1.5 text-xs font-semibold"><CheckCircle2 className="w-3.5 h-3.5" />Recent reviews</span>}
      />
    </div>
  );
}
